import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { fetchBidHistory } from '@/redux/api/bidHistory';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_BID_HISTORY_FAIL = 'la/domain/bidHistory/LOAD_FAIL';
export const LOAD_BID_HISTORY_REQUEST = 'la/domain/bidHistory/LOAD_REQUEST';
export const LOAD_BID_HISTORY_SUCCESS = 'la/domain/bidHistory/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_BID_HISTORY_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.itemId]),
            };
        case LOAD_BID_HISTORY_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_BID_HISTORY_SUCCESS:
            time = action.meta.actionTime;
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded, [action.meta.itemId]: time };
            loading = cloneDeep(state.loading);
            existing[action.meta.itemId] = action.payload;
            loading = difference(loading, [action.meta.itemId]);
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.bidHistory;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getBidHistory = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || []);

const getLoadTimeForBidHistory = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isBidHistoryLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetchBidHistory = createSelector(
    [idSelector, getBidHistory, getLoadTimeForBidHistory, isBidHistoryLoading],
    (itemId, bidHistory, loaded, loading) => {
        if (!itemId) {
            return false;
        }
        if (bidHistory) {
            const time = Date.now();
            const diff = time - loaded;
            if (diff < REDUX_STORE_TIME) {
                return false;
            }
        }
        return !loading;
    }
);

/* ACTION CREATORS */
export const fetchBidHistoryIfNeeded = (itemId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (shouldFetchBidHistory(state, itemId)) {
        try {
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            dispatch({
                meta: { actionTime: Date.now() },
                payload: itemId,
                type: LOAD_BID_HISTORY_REQUEST,
            });
            const response = await fetchBidHistory({ authToken, deployment, itemId });
            dispatch({
                meta: { actionTime: Date.now(), itemId },
                payload: response.data,
                type: LOAD_BID_HISTORY_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: { itemId },
                payload: error,
                type: LOAD_BID_HISTORY_FAIL,
            });
        }
    } else {
        return Promise.resolve();
    }
};
